/* S19a: 最近チェックした商品 — 記録 + ハイドレーション（TOP / PLP / PDP 共用・各面 10 件
 * 〔0713-G5 で 6/4/5 → 10 に統一・tol-slider スライダー化〕）。
 *
 * 役割:
 *  1) 記録: data-tol-rv-record="<handle>" を持つページ（PDP 等）で現在の商品 handle を
 *     localStorage キー tol:rv:v1 に { v:1, items:[{ h:<handle>, t:<epoch_ms> }] } で積む。
 *     handle + timestamp のみ保存（price/title/image は保存しない = 価格陳腐化＝虚偽 UI 回避・
 *     二重メンテ回避）。新しい順・同 handle 除去 → 先頭 unshift → 上限 20 → 期限切れ間引き（30日）。
 *  2) ハイドレーション: [data-tol-recently-viewed] セクションで localStorage の handle を
 *     data-tol-rv-limit / data-tol-rv-exclude でフィルタ → handle ごと
 *     <root>/products/<handle>?sections=tol-rv-card を after-paint（load 後 +
 *     requestIdleCallback）で Promise.allSettled フェッチ → 返却 HTML から .tol-product-card を
 *     抽出し subset CSS（<link rel=stylesheet>/<style>）を <head> へ注入 → grid に流し込む。
 *     stale handle は除去。
 *
 * fail-closed（Copilot BLOCKER 決定3 + G2 critical B-1）:
 *  対象は外側ラッパー（schema class 付き .shopify-section）。ラッパーは CSS 既定で display:none、
 *  JS が実履歴をハイドレート成功したときだけ .tol-rv-revealed を付けて開示する。
 *  履歴ゼロ / 全除外 / フェッチ全滅 / localStorage 失敗 / JS 不在 のいずれでもラッパーごと非表示 ＝
 *  架空カードも paper-light 空帯も一切出ない（内側 div だけ隠すと背景+py がラッパーに残る罠を回避）。
 *
 * LCP 非干渉（Copilot HIGH 決定2）:
 *  フェッチは window load 後 + requestIdleCallback（after-paint）まで遅延（クリティカル期間に
 *  5-6 件の section render を投げない）。返却まで section は hidden のため CLS も出さない。
 *  ※ IntersectionObserver による viewport ゲートは不可: fail-closed の初期 display:none 要素は
 *    交差判定されず IO が永久に発火しないため（実機で確認・S19a）。
 *
 * 公式根拠:
 *  - Section Rendering API: /products/<handle>?sections=<type> → JSON { "<type>": "<html>" }・
 *    設定値は query で渡せない（既定値）・最大 5 セクション
 *    （https://shopify.dev/docs/api/ajax/section-rendering ）。handle が違う = URL が違うため
 *    1 リクエストに束ねられず handle ごとフェッチ。
 *  - sections= は JSON 返却（section_id= は raw HTML）= JSON 経路で扱う。
 *  - URL は locale/root context（data-tol-rv-root = routes.root_url）で組む（ja/zh-TW/market を保つ）。
 *  ※ Recently viewed のテーマ向け公式実装は公式未確認（localStorage 方式は一般慣行）。
 *
 * 規律（tol-tabs.js / tol-ugc-modal.js 前例）: custom element は作らない・アロー IIFE +
 * window.__tolRecentlyViewedLoaded ガード + data-*-init ガード + AbortController teardown +
 * shopify:section:load/unload 対応。Dawn 本体無改修・tol- 隔離。localStorage 例外は握りつぶし。
 */
(() => {
  /* 同一 src の <script> はセクション複数配置で複数回実行される（フェッチは1回・実行はタグごと） */
  if (window.__tolRecentlyViewedLoaded) return;
  window.__tolRecentlyViewedLoaded = true;

  const STORAGE_KEY = 'tol:rv:v1';
  const MAX_ITEMS = 20;
  const MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000; /* 30 日 */
  const SECTION_TYPE = 'tol-rv-card';

  /* ---- localStorage（quota/security 例外を握りつぶし機能を黙って無効化） ---- */

  const readStore = () => {
    try {
      const raw = window.localStorage.getItem(STORAGE_KEY);
      if (!raw) return [];
      const data = JSON.parse(raw);
      if (!data || data.v !== 1 || !Array.isArray(data.items)) return [];
      /* G2 Copilot BLOCKER: read で { h, t } のみに正規化（新オブジェクトで再構築）。
         どこかで title/price/image 等の余分フィールドが混入しても read 時点で削ぎ落とし、
         後続の writeStore が正規化済みのみ書き戻す ＝「handle + timestamp のみ保存」の不変条件を
         消費・記録の両経路で強制（価格陳腐化＝虚偽 UI の作り込み防止）。 */
      return data.items
        .filter((it) => it && typeof it.h === 'string' && typeof it.t === 'number')
        .map((it) => ({ h: it.h, t: it.t }));
    } catch (e) {
      return [];
    }
  };

  const writeStore = (items) => {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify({ v: 1, items: items }));
    } catch (e) {
      /* private mode / quota 等 — 黙って諦める（fail-closed: 描画もされないだけ） */
    }
  };

  /* 期限切れ間引き + 上限 truncate。now は呼び出し側から渡す（テスト容易性 + 単一呼び出し） */
  const prune = (items, now) =>
    items.filter((it) => now - it.t <= MAX_AGE_MS).slice(0, MAX_ITEMS);

  const record = (handle) => {
    if (!handle) return;
    const now = Date.now();
    let items = readStore();
    /* 同 handle 除去 → 先頭 unshift（新しい順） */
    items = items.filter((it) => it.h !== handle);
    items.unshift({ h: handle, t: now });
    writeStore(prune(items, now));
  };

  /* stale handle（削除/非公開でフェッチ失敗）を storage から除去 */
  const removeHandles = (stale) => {
    if (!stale || !stale.size) return;
    const items = readStore().filter((it) => !stale.has(it.h));
    writeStore(items);
  };

  /* ---- subset CSS 注入（返却 HTML 内の <link rel=stylesheet> / <style> を <head> へ・重複防止） ---- */

  const injectedHrefs = new Set();
  const injectStyles = (fragment) => {
    fragment.querySelectorAll('link[rel="stylesheet"], style').forEach((node) => {
      if (node.tagName === 'LINK') {
        const href = node.getAttribute('href');
        if (!href || injectedHrefs.has(href)) return;
        /* 既に <head> に同 href があれば skip（各セクションが同 tol-product-card.css を既に読む） */
        if (document.querySelector('link[rel="stylesheet"][href="' + CSS.escape(href) + '"]')) {
          injectedHrefs.add(href);
          return;
        }
        injectedHrefs.add(href);
        document.head.appendChild(node.cloneNode(true));
      } else {
        document.head.appendChild(node.cloneNode(true));
      }
    });
  };

  /* ---- ハイドレーション ---- */

  const hydrate = (section, signal) => {
    if (section.dataset.tolRvHydrated) return;
    section.dataset.tolRvHydrated = 'true';

    const root = section.dataset.tolRvRoot || '/';
    const limit = parseInt(section.dataset.tolRvLimit, 10) || 6;
    const exclude = section.dataset.tolRvExclude || '';
    const grid = section.querySelector('[data-tol-rv-grid]');
    if (!grid) {
      hideSection(section);
      return;
    }

    /* H-1（G2 critical）: 消費（read）パスにも age/上限 prune を掛ける。prune は record()（書込）
       のみだと、PDP を訪問せず書込が発生しないセッションで期限切れ handle を fetch し続ける。
       期限切れで間引いた結果が元と変われば storage に書き戻して掃除する（write が無い再訪でも掃除）。 */
    const now = Date.now();
    const stored = readStore();
    const pruned = prune(stored, now);
    if (pruned.length !== stored.length) writeStore(pruned);

    let handles = pruned
      .map((it) => it.h)
      .filter((h) => h && h !== exclude);
    /* 念のため重複除去（記録側で除去済みだが防御的） */
    handles = handles.filter((h, i) => handles.indexOf(h) === i).slice(0, limit);

    if (!handles.length) {
      hideSection(section);
      return;
    }

    /* root は routes.root_url（locale/market サブパス込み・末尾スラッシュ有無を吸収） */
    const base = root.replace(/\/+$/, '');
    const urlFor = (h) => base + '/products/' + encodeURIComponent(h) + '?sections=' + SECTION_TYPE;

    /* G2 Copilot HIGH（実機仕様で再設計）: 除去は「商品の確定的不在」に限定し、一時障害では保持する。
       実機仕様: /products/<handle>?sections=tol-rv-card は handle が無くても HTTP 200 + 空 HTML
       （card なし）を返す（404 ではない・実測）。一方サーバー/ネットワーク不調は 5xx・例外。
       → 確定的不在 = 404 または「200 だが card なし」（＝商品が削除/非公開で空 section）。これは除去。
       → 一時障害 = ネットワーク例外・5xx（fetch reject / !res.ok）・JSON 失敗。これは描画せず storage 保持。
       これで「回線不調で履歴が消える」（Copilot HIGH）を防ぎつつ stale 商品の掃除は維持する。
       ok=false の判定は then 内で行い、reject に倒して保持側へ送る（gone と混同しない）。 */
    Promise.allSettled(
      handles.map((h) =>
        fetch(urlFor(h), { signal }).then((res) => {
          if (res.status === 404) return { h: h, gone: true }; /* 確定的不在 */
          if (!res.ok) throw new Error('status ' + res.status); /* 5xx 等 = reject = 保持 */
          return res.json().then((data) => ({ h: h, html: data[SECTION_TYPE] }));
        })
      )
    ).then((results) => {
      if (signal.aborted) return;
      const parser = new DOMParser();
      const stale = new Set();
      const frag = document.createDocumentFragment();
      let count = 0;

      /* handles の並び順（新しい順）を保つため results を index で走査 */
      results.forEach((r, i) => {
        const handle = handles[i];
        /* rejected（ネットワーク例外 / 5xx / JSON 失敗）= 一時障害の可能性 → 描画せず storage 保持 */
        if (r.status !== 'fulfilled' || !r.value) return;
        /* 404 = 商品の確定的不在 → stale 除去 */
        if (r.value.gone) {
          stale.add(handle);
          return;
        }
        const html = r.value.html;
        const doc = typeof html === 'string' ? parser.parseFromString(html, 'text/html') : null;
        const card = doc ? doc.querySelector('.tol-product-card') : null;
        /* 200 + card あり → 描画。200 + card なし（商品が削除/非公開で空 section）→ 確定的不在で stale 除去。 */
        if (!card) {
          stale.add(handle);
          return;
        }
        injectStyles(doc);
        frag.appendChild(card);
        count += 1;
      });

      removeHandles(stale);

      if (!count) {
        hideSection(section); /* 全滅 = fail-closed */
        return;
      }
      grid.replaceChildren(frag);
      revealSection(section);
      /* 0713-G5: tol-slider へ再計測を通知。非同期カード注入は「scrollWidth のみの変化」で
         ResizeObserver 非検知（tol-slider DOM 契約）＝reveal（display:none 解除）と同一タスクで
         面側から dispatch する。ラッパー [data-tol-slider] は grid の親側 = grid.closest で辿る
         （section.closest は不可: .tol-slider は section 要素の内側にある）。未 init /
         ラッパー不在（スライダー未適用面）は no-op。 */
      const sliderWrap = grid.closest('[data-tol-slider]');
      if (sliderWrap) sliderWrap.dispatchEvent(new CustomEvent('tol:slider:refresh'));
    });
  };

  /* fail-closed の対象は外側ラッパー（schema class 付きの .shopify-section div）。内側 div だけ
     隠すと paper-light 背景 + py がラッパーに残り空帯になる（G2 critical B-1・実機再現）。
     ラッパーは CSS 既定で display:none、描画成功時のみ .tol-rv-revealed を付けて開示する
     （hidden 属性でなく class＝[hidden] を打ち消す CSS への耐性・reviewer I-2）。
     ラッパーが見つからなければ内側 div を対象にフォールバック（保険）。 */
  const wrapperOf = (section) => section.closest('.shopify-section') || section;
  const hideSection = (section) => {
    wrapperOf(section).classList.remove('tol-rv-revealed');
  };
  const revealSection = (section) => {
    wrapperOf(section).classList.add('tol-rv-revealed');
  };

  /* ---- 起動 ---- */

  /* after-paint まで遅延してから cb を実行（クリティカル期間に section render fetch を投げない）。
     IntersectionObserver による viewport ゲートは使わない: fail-closed のため section は初期
     display:none で、display:none 要素は IntersectionObserver が交差判定せず永久に発火しない
     （実機で確認・S19a）。LCP 非干渉は「load 後 + requestIdleCallback で遅延」で担保する。 */
  const afterPaint = (cb, signal) => {
    const run = () => {
      if (signal.aborted) return;
      if ('requestIdleCallback' in window) {
        const id = window.requestIdleCallback(() => cb(), { timeout: 2000 });
        signal.addEventListener('abort', () => window.cancelIdleCallback(id));
      } else {
        const t = window.setTimeout(cb, 200);
        signal.addEventListener('abort', () => window.clearTimeout(t));
      }
    };
    /* document が既に load 済みなら即 idle へ・未了なら load 後 */
    if (document.readyState === 'complete') {
      run();
    } else {
      window.addEventListener('load', run, { once: true, signal: signal });
    }
  };

  const initSection = (section, signal) => {
    if (section.dataset.tolRvInit) return;
    section.dataset.tolRvInit = 'true';
    afterPaint(() => hydrate(section, signal), signal);
  };

  const init = (rootEl) => {
    /* 記録: data-tol-rv-record を持つ要素があれば現在商品を記録（PDP 等） */
    const recorder = rootEl.querySelector('[data-tol-rv-record]') ||
      (rootEl.matches && rootEl.matches('[data-tol-rv-record]') ? rootEl : null);
    if (recorder) record(recorder.dataset.tolRvRecord);

    /* ハイドレーション対象セクション */
    const sections = Array.from(rootEl.querySelectorAll('[data-tol-recently-viewed]'));
    if (rootEl.matches && rootEl.matches('[data-tol-recently-viewed]')) sections.push(rootEl);
    sections.forEach((section) => {
      const abort = new AbortController();
      section._tolRv = { abort };
      initSection(section, abort.signal);
    });
  };

  init(document);
  document.addEventListener('shopify:section:load', (e) => init(e.target));
  document.addEventListener('shopify:section:unload', (e) => {
    e.target.querySelectorAll('[data-tol-recently-viewed]').forEach((section) => {
      if (section._tolRv) section._tolRv.abort.abort();
    });
  });
})();
